import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Gift,
  CheckCircle2,
  Sparkles,
  CreditCard,
  Link,
  Palette,
  Shield,
  Mail,
  BarChart3,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { BackgroundPattern } from '@/components/ui/BackgroundPattern';

interface WebformPaymentsOnboardingProps {
  onEnable: () => void;
  isEnabling?: boolean;
}

export function WebformPaymentsOnboarding({ onEnable, isEnabling = false }: WebformPaymentsOnboardingProps) {
  const [activeFeature, setActiveFeature] = useState<number | null>(null);

  const features = [
    {
      icon: CreditCard,
      title: 'Accept Payments Anywhere',
      description: 'Create secure payment forms that work on any device. Accept credit cards and ACH payments with industry-leading security.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Link,
      title: 'Shareable Payment Links',
      description: 'Generate unique URLs for each payment form. Share via email, text, social media, or embed on your website.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Palette,
      title: 'Branded Experience',
      description: 'Customize colors, logos, and messaging to match your brand. Create a professional payment experience.',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'PCI-compliant payment processing with 256-bit SSL encryption. Your customers\' data is always protected.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: Mail,
      title: 'Automated Receipts',
      description: 'Send professional email confirmations automatically. Customize thank you messages and redirect URLs.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      icon: BarChart3,
      title: 'Real-Time Analytics',
      description: 'Track all submissions in one place. Filter by date, export to CSV, and monitor payment status.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
  ];

  const useCases = [
    'Service deposits and retainers',
    'Membership fees and subscriptions',
    'Event registrations and tickets',
    'Donations and fundraising',
    'Product pre-orders',
    'Course enrollments',
  ];

  return (
    <div className="relative min-h-screen py-12">
      <BackgroundPattern />

      <div className="relative z-10 mx-auto max-w-6xl px-4">
        {/* Hero Section */}
        <div className="mb-16 text-center">
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 animate-pulse rounded-full bg-primary/20 blur-2xl"></div>
              <div className="relative rounded-full bg-gradient-to-br from-primary to-purple-600 p-6">
                <Gift className="h-16 w-16 text-white" />
              </div>
            </div>
          </div>

          <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
            <Sparkles className="mr-1 h-3 w-3" />
            New Feature
          </Badge>

          <h1 className="mb-4 text-5xl font-bold tracking-tight">
            Webform Payments
          </h1>

          <p className="mx-auto mb-8 max-w-2xl text-xl text-muted-foreground">
            Create beautiful, secure payment forms in minutes. Accept payments from anyone, anywhere, without writing a single line of code.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              size="lg"
              onClick={onEnable}
              disabled={isEnabling}
              className="group h-14 px-8 text-lg shadow-lg transition-all hover:scale-105 hover:shadow-xl"
            >
              {isEnabling ? (
                <>
                  <span className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Enabling...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-5 w-5" />
                  Enable Webform Payments
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="h-14 px-8 text-lg"
              onClick={() => {
                const featuresSection = document.getElementById('features');
                featuresSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Learn More
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div id="features" className="mb-16">
          <div className="mb-8 text-center">
            <h2 className="mb-3 text-3xl font-bold">Everything You Need</h2>
            <p className="text-lg text-muted-foreground">
              Powerful features designed for seamless payment collection
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = activeFeature === index;

              return (
                <Card
                  key={index}
                  className={`group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl ${
                    isActive ? 'ring-2 ring-primary' : ''
                  }`}
                  onMouseEnter={() => setActiveFeature(index)}
                  onMouseLeave={() => setActiveFeature(null)}
                >
                  <CardContent className="p-6">
                    <div
                      className={`mb-4 inline-flex rounded-lg p-3 transition-all duration-300 ${
                        isActive ? feature.bgColor : 'bg-muted'
                      }`}
                    >
                      <Icon
                        className={`h-6 w-6 transition-colors duration-300 ${
                          isActive ? feature.color : 'text-muted-foreground'
                        }`}
                      />
                    </div>

                    <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Use Cases */}
        <Card className="mb-16 overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <CardContent className="p-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="mb-4 text-2xl font-bold">Perfect For</h2>
                <div className="space-y-3">
                  {useCases.map((useCase, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium">{useCase}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col justify-center rounded-lg bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-100">
                    Quick Setup
                  </Badge>
                  <h3 className="mb-2 text-xl font-bold">Get Started in 3 Steps</h3>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      1
                    </span>
                    <span>Enable Webform Payments to unlock the feature</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      2
                    </span>
                    <span>Customize your form with your brand colors and messaging</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                      3
                    </span>
                    <span>Share your payment link and start accepting payments</span>
                  </li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="overflow-hidden border-2 border-primary bg-gradient-to-br from-primary to-purple-600 text-white">
          <CardContent className="p-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Ready to Start Accepting Payments?</h2>
            <p className="mb-8 text-lg text-white/90">
              Enable Webform Payments now and create your first payment form in minutes.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={onEnable}
              disabled={isEnabling}
              className="group h-14 px-8 text-lg shadow-lg transition-all hover:scale-105 hover:shadow-xl"
            >
              {isEnabling ? (
                <>
                  <span className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                  Enabling...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-5 w-5" />
                  Enable Webform Payments
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
